/**
 * Series detail on the TV — the only route into episodes.
 *
 * Same endpoint and same data as the web page. Two things are laid out differently, and
 * both follow from the remote:
 *
 *  - Seasons are a horizontal strip and episodes a vertical list, so Left/Right changes
 *    the season and Up/Down walks the episodes. A grid of episodes would put the two
 *    movements on the same axis and make "next episode" a guess.
 *  - Episode rows are wide and short. At three metres the useful target is the row, not a
 *    thumbnail, and a row that spans the content column cannot be missed sideways.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { SeriesDetailDto } from '@filmodasi/shared';
import { useApi } from '../../../api/hooks';
import { ErrorBox, Empty, Spinner } from '../../../components/ui';
import { Icon } from '../../../components/Icon';
import { resumeEpisode, watchedPercent } from '../../../catalog/episodeProgress';
import { seriesCounts } from '../../../catalog/seriesCounts';
import { useFocusable } from '../focus/useFocusable';
import { TvRecommend } from '../TvRecommend';

function SeasonChip({
  id,
  label,
  count,
  active,
  onSelect,
}: {
  id: string;
  label: string;
  count: number;
  active: boolean;
  onSelect: () => void;
}) {
  const { ref, focused } = useFocusable<HTMLButtonElement>({ id, group: 'season', onSelect });
  return (
    <button
      ref={ref}
      type="button"
      className={`tvchip${focused ? ' is-focused' : ''}${active ? ' is-active' : ''}`}
      onClick={onSelect}
    >
      {label} ({count})
    </button>
  );
}

function EpisodeRow({
  id,
  number,
  title,
  playable,
  percent,
  completed,
  resume,
  autoFocus,
  onSelect,
}: {
  id: string;
  number: number | null;
  title: string;
  playable: boolean;
  /** 0–100. Drawn full for a finished episode — see `watchedPercent`. */
  percent: number;
  completed: boolean;
  resume: boolean;
  autoFocus: boolean;
  onSelect: () => void;
}) {
  const { ref, focused } = useFocusable<HTMLDivElement>({
    id,
    group: 'episode',
    onSelect,
    autoFocus,
  });
  return (
    <div
      ref={ref}
      role="button"
      /*
       * The state goes into the accessible name rather than being left to the visuals.
       * `aria-label` overrides the row's contents entirely, so a tick and a badge added
       * inside it would otherwise be announced as nothing at all.
       */
      aria-label={[title, completed ? 'izlendi' : null, resume ? 'devam et' : null]
        .filter(Boolean)
        .join(', ')}
      className={`tvep${focused ? ' is-focused' : ''}${resume ? ' is-resume' : ''}`}
      onClick={onSelect}
    >
      <span className="tvep__num">{number ?? '—'}</span>
      <span className="tvep__title">{title}</span>
      {/* Shown rather than hidden: on a television AVPlay usually handles exactly these
          containers, so the row stays selectable and this is a note, not a refusal. */}
      {!playable && <span className="tvep__badge">özel biçim</span>}
      {resume && <span className="tvep__badge tvep__badge--resume">Devam et</span>}
      {completed && <Icon name="check" size={1} className="tvep__tick" />}
      {percent > 0 && (
        <span className="tvep__bar" aria-hidden>
          <span style={{ width: `${percent}%` }} />
        </span>
      )}
    </div>
  );
}

export function TvSeriesDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, error, loading, reload } = useApi<SeriesDetailDto>(
    id ? `/api/catalog/series/detail/${id}` : null,
  );
  const [season, setSeason] = useState<number | null>(null);

  const resume = useMemo(
    () => (data ? resumeEpisode(data.episodes, data.progress) : null),
    [data],
  );

  const activeSeason =
    season ?? resume?.seasonNumber ?? data?.seasons[0]?.seasonNumber ?? 1;
  const episodes = useMemo(
    () => data?.episodes.filter((e) => (e.seasonNumber ?? 1) === activeSeason) ?? [],
    [data, activeSeason],
  );

  if (loading && !data) return <Spinner />;
  if (error) return <ErrorBox message={error} onRetry={reload} />;
  if (!data) return null;

  return (
    <>
      <div className="tvdetail">
        {data.series.poster && (
          <img src={data.series.poster} alt="" className="tvdetail__poster" loading="lazy" />
        )}
        <div>
          <h1 className="tv__heading">{data.series.title}</h1>
          <p className="tv__section">
            {[data.series.year, seriesCounts(data.seasons), data.series.genres.join(' · ')]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {data.series.plot && <p className="tvdetail__plot">{data.series.plot}</p>}
          {/* The series, not an episode: "S02B07" recommended to somebody who has not
              started the show is not a recommendation. */}
          <div className="tvitem__actions">
            <TvRecommend itemId={data.series.id} />
          </div>
        </div>
      </div>

      {data.seasons.length === 0 ? (
        <Empty>Bu dizinin bölümleri henüz alınmadı.</Empty>
      ) : (
        <>
          <div className="tvchips">
            {data.seasons.map((s, i) => (
              <SeasonChip
                key={s.id}
                id={`season:${i}`}
                label={s.title}
                count={s.episodeCount}
                active={s.seasonNumber === activeSeason}
                onSelect={() => setSeason(s.seasonNumber)}
              />
            ))}
          </div>

          <div className="tveps">
            {episodes.map((ep, i) => (
              <EpisodeRow
                key={ep.id}
                id={`ep:${i}`}
                number={ep.episodeNumber ?? null}
                title={ep.title}
                playable={ep.playable}
                percent={watchedPercent(data.progress[ep.id])}
                completed={data.progress[ep.id]?.completed ?? false}
                resume={ep.id === resume?.id}
                /*
                 * The point of the whole feature on a television: open a series and the
                 * remote is already on the episode you are up to, in the right season,
                 * rather than on episode 1 of season 1 with four seasons to walk through.
                 *
                 * Gated on `season === null` so it fires once. Without that, choosing a
                 * season would remount this list with `autoFocus` still set on a row in a
                 * *different* season — nothing to focus in the visible list, and on the
                 * season that does contain it, focus torn back out of the chip strip the
                 * viewer is still using.
                 */
                autoFocus={season === null && ep.id === resume?.id}
                onSelect={() => navigate(`/watch/${ep.id}`)}
              />
            ))}
          </div>
        </>
      )}
    </>
  );
}
